import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, ExternalLink, Radio, Handshake } from "lucide-react";
import { useTimezone } from "@/contexts/TimezoneContext";
import { convertShowTimes, BASE_TZ, zoneLabel } from "@/lib/timezone";
import TimezoneSelector from "@/components/TimezoneSelector";
import { CollabBadge } from "@/components/CollabBadge";

const DAY_SHORT = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const CATEGORY_COLORS = {
  Talk: "#007AFF", Music: "#FF3B30", Sports: "#32D74B", Podcast: "#FFD60A",
  News: "#AF52DE", Comedy: "#FF9500", Interview: "#5AC8FA", "Live Event": "#FF2D92",
};
function categoryColor(cat) { return CATEGORY_COLORS[cat] || "#FF3B30"; }

// If show is a DAILY special, expand to all 7 days automatically.
function airingDays(s) {
  if (s.is_special && s.special_frequency === "daily") return [0, 1, 2, 3, 4, 5, 6];
  if (Array.isArray(s.days_of_week) && s.days_of_week.length > 0) return s.days_of_week;
  return [s.day_of_week ?? 0];
}

// Convert a show's anchor day + times from the base (EST) timezone into `tz`.
// Returns { days: [], start_time, end_time }.
function toTzView(s, tz) {
  const base = airingDays(s);
  if (!s.start_time || !s.end_time || !tz || tz === BASE_TZ) {
    return { days: base, start_time: s.start_time, end_time: s.end_time };
  }
  const anchor = base[0];
  const { day: newAnchor, start_time, end_time } = convertShowTimes(anchor, s.start_time, s.end_time, tz);
  const shift = ((newAnchor - anchor) % 7 + 7) % 7;
  const days = base.map((d) => (d + shift) % 7).sort((a, b) => a - b);
  return { days, start_time, end_time };
}

function ShowCard({ s, timezone }) {
  const color = categoryColor(s.category);
  return (
    <Link to={`/shows/${s.id}`} className="bg-[var(--ff-surface)] hover:bg-[var(--ff-surface-hover)] border border-[var(--ff-border)] transition-colors group flex flex-col" data-testid={`show-card-${s.id}`}>
      <div className="relative aspect-[16/10] overflow-hidden" style={{ background: `linear-gradient(135deg, ${color}33 0%, ${color}11 100%)` }}>
        {s.thumbnail_url ? (
          <img src={s.thumbnail_url} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="font-display text-6xl opacity-30" style={{ color }}>
              {s.title.split(" ").map((w) => w[0]).slice(0, 3).join("")}
            </div>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1">
          {s.is_collab && <CollabBadge solid testId={`show-card-collab-${s.id}`} />}
          {s.is_special && (
            <span className="font-mono-ui text-[0.6rem] uppercase tracking-[0.1em] px-1.5 py-0.5 bg-[#AF52DE] text-white font-bold">
              ✦ {s.special_duration_hours || ""}H {s.special_frequency === "daily" ? "DAILY " : s.special_frequency === "weekly" ? "WEEKLY " : ""}SPECIAL
            </span>
          )}
          {s.is_premiere && <span className="font-mono-ui text-[0.6rem] uppercase tracking-[0.1em] px-1.5 py-0.5 bg-[#FFD60A] text-black font-bold">★ PREMIERE</span>}
          {s.is_finale && <span className="font-mono-ui text-[0.6rem] uppercase tracking-[0.1em] px-1.5 py-0.5 bg-[var(--ff-red)] text-white font-bold">⬢ FINALE</span>}
        </div>
        {s.category && (
          <span className="absolute top-3 right-3 font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] px-2 py-1 border font-bold" style={{ background: color, color: "white", borderColor: color }}>
            {s.category}
          </span>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="font-display text-2xl mb-2 leading-tight">{s.title}</div>
        <div className="font-mono-ui text-xs text-[var(--ff-text-dim)] mb-3" data-testid={`show-times-${s.id}`}>
          {(() => {
            const v = toTzView(s, timezone);
            return `${v.days.map((d) => DAY_SHORT[d]).join(" · ")} / ${v.start_time}–${v.end_time}`;
          })()}
        </div>
        {s.description && <p className="text-sm text-[var(--ff-text-dim)] line-clamp-2 flex-1">{s.description}</p>}
        {s.hosts && s.hosts.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1">
            {s.hosts.slice(0, 3).map((h) => (
              <span key={h} className="font-mono-ui text-[0.65rem] uppercase tracking-[0.12em] text-[var(--ff-text-dim)]">{h}</span>
            ))}
          </div>
        )}
        {s.season_number && (
          <div className="mt-3 font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">
            SEASON {s.season_number}
            {s.season_start && s.season_end && ` · ${s.season_start} → ${s.season_end}`}
          </div>
        )}
        <div className="mt-4 pt-3 border-t border-[var(--ff-border)] flex items-center justify-between text-xs font-mono-ui text-[var(--ff-text-dim)]">
          <span className="flex items-center gap-1"><Radio size={12} /> {s.is_recurring ? "WEEKLY" : "ONE-OFF"}</span>
          <ArrowRight size={14} className="text-[var(--ff-red)] group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

export default function ShowsIndex() {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("ALL");
  const { timezone, isBase } = useTimezone();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/shows");
        setShows(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const categories = useMemo(() => Array.from(new Set(shows.map((s) => s.category).filter(Boolean))).sort(), [shows]);
  const filtered = filterCat === "ALL" ? shows : shows.filter((s) => s.category === filterCat);
  const own = filtered.filter((s) => !s.is_collab);
  const collab = filtered.filter((s) => s.is_collab);

  return (
    <div data-testid="shows-index-page" className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24">
      <div className="label-eyebrow mb-6 text-[var(--ff-red)]">▮ NETWORK / SHOWS</div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <h1 className="font-display text-5xl lg:text-8xl">SHOWS</h1>
          <p className="text-[var(--ff-text-dim)] text-lg mt-4 max-w-2xl">
            Every original show across the Freshy Fresh network. Prime time, weekly recurring, premieres, and season finales.
          </p>
          <div className="mt-4">
            <TimezoneSelector />
            {!isBase && (
              <div className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-2" data-testid="shows-tz-note">
                Times shown in <span className="text-white">{zoneLabel(timezone)}</span> · Baseline: US Eastern
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="category-filters">
          <button onClick={() => setFilterCat("ALL")} className={`font-mono-ui text-xs uppercase tracking-[0.15em] px-3 py-2 border transition-colors ${filterCat === "ALL" ? "bg-white text-black border-white" : "border-[var(--ff-border)] text-[var(--ff-text-dim)] hover:text-white"}`} data-testid="filter-all">
            All ({shows.length})
          </button>
          {categories.map((c) => (
            <button key={c} onClick={() => setFilterCat(c)} className={`font-mono-ui text-xs uppercase tracking-[0.15em] px-3 py-2 border transition-colors flex items-center gap-2 ${filterCat === c ? "text-white" : "text-[var(--ff-text-dim)] hover:text-white"}`} style={{ borderColor: filterCat === c ? categoryColor(c) : "var(--ff-border)", background: filterCat === c ? categoryColor(c) : "transparent" }} data-testid={`filter-${c.toLowerCase()}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: filterCat === c ? "white" : categoryColor(c) }} />
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-[var(--ff-text-dim)]">Loading shows...</div>
      ) : filtered.length === 0 ? (
        <div className="surface-card p-12 text-center text-[var(--ff-text-dim)]">
          No shows yet. Site admin can add shows from the dashboard.
        </div>
      ) : (
        <>
          {own.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="shows-own-grid">
              {own.map((s) => <ShowCard key={s.id} s={s} timezone={timezone} />)}
            </div>
          ) : (
            <div className="surface-card p-8 text-center text-sm text-[var(--ff-text-dim)]" data-testid="shows-own-empty">No Freshy Fresh originals in this category.</div>
          )}
          {collab.length > 0 && (
            <section className="mt-16" data-testid="shows-collab-section">
              <div className="mb-6 pb-3 border-b border-[#5AC8FA]/30">
                <div className="label-eyebrow text-[#5AC8FA] flex items-center gap-2" data-testid="shows-collab-heading">
                  <Handshake size={12} /> ▮ COLLABORATIONS · {collab.length}
                </div>
                <p className="text-sm text-[var(--ff-text-dim)] mt-1">Shows we make with partners. Not Freshy Fresh Productions LLC property.</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="shows-collab-grid">
                {collab.map((s) => <ShowCard key={s.id} s={s} timezone={timezone} />)}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
